import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Gear,
  SignOut,
  BookOpen,
  Database,
  Lightbulb,
  MagnifyingGlass,
  BookOpenText,
  Newspaper,
} from '@phosphor-icons/react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useAuth } from '@/lib/auth';
import { useGetUserDetails } from '@/features/settings/api/getUserDetails';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { ReactComponent as Dashboard } from '@/assets/layouts.svg';
import { ReactComponent as DashboardFilled } from '@/assets/layouts-filled.svg';
import useExploreStore from '@/features/reports/store/useExploreStore';
import { useShallow } from 'zustand/react/shallow';
import isRouteActive from '@/utils/isRouteActive';

export const NEW_LAYOUT_UI = ['explore', 'deep-dive', 'dashboard'];

export const Sidebar = () => {
  const { logout, user } = useAuth();

  const storedEmail = localStorage.getItem('email');

  const { data } = useGetUserDetails({
    config: {
      staleTime: Infinity,
    },
  });

  const isDeepDiveEnabled = useFeatureFlagEnabled('deep-dive');
  const isDiscoverEnabled = useFeatureFlagEnabled('discover');
  const isPetaReportsEnabled = useFeatureFlagEnabled('peta-reports');
  const isTemplatesEnabled = useFeatureFlagEnabled('templates');
  const isSettingsIntegrationsEnabled = useFeatureFlagEnabled(
    'settings-integrations'
  );
  const isDashboardsEnabled = useFeatureFlagEnabled('dashboards');

  const { resetResultsPageState, resetRightPanelState } = useExploreStore(
    useShallow((state) => ({
      resetRightPanelState: state.resetRightPanelState,
      resetResultsPageState: state.resetResultsPageState,
    }))
  );

  function getInitials(fullName) {
    // Function to extract initials from a string
    const extractInitials = (str) =>
      str
        .split(/[_.]/)
        .map((part) => part[0])
        .join('')
        .toUpperCase();

    if (fullName) {
      const names = fullName?.split(' ');
      const initials = names
        .map((name, index) =>
          index === 0 || index === names?.length - 1 ? name[0] : ''
        )
        .filter(Boolean);

      // If no initials are found from the name, use the email initials
      if (initials.length === 0 && storedEmail) {
        const emailInitials = extractInitials(storedEmail?.split('@')[0]);
        return emailInitials;
      }

      return initials.join('').toUpperCase();
    } else if (storedEmail) {
      // If name is not available, use the email initials
      const emailInitials = extractInitials(storedEmail?.split('@')[0]);
      return emailInitials;
    }
    return '';
  }

  let primaryLinks = [
    {
      name: 'Explore',
      to: 'explore',
      icon: MagnifyingGlass,
      accessibleBy: ['admin', 'user'],
      enabled: true,
      trigger: () => {
        resetResultsPageState();
        resetRightPanelState();
      },
    },
    {
      name: 'Deep Dive',
      to: 'deep-dive',
      icon: Lightbulb,
      accessibleBy: ['admin', 'user'],
      enabled: true,
    },
    {
      name: 'Discover',
      to: 'discover',
      icon: BookOpenText,
      accessibleBy: ['admin'],
      enabled: isDiscoverEnabled,
    },
    {
      name: 'Dashboard',
      to: 'dashboard',
      icon: {
        active: DashboardFilled,
        inactive: Dashboard,
      },
      accessibleBy: ['admin', 'user'],
      enabled: isDashboardsEnabled,
    },
    {
      name: 'Templates',
      to: 'templates',
      icon: BookOpen,
      accessibleBy: ['admin'],
      enabled: isTemplatesEnabled,
    },
    {
      name: 'PetaReports',
      to: 'peta-reports',
      icon: Newspaper,
      accessibleBy: ['admin'],
      enabled: isPetaReportsEnabled,
    },
    {
      name: 'Data Hub',
      to: 'data-catalog',
      icon: Database,
      accessibleBy: ['admin'],
      enabled: true,
    },
    {
      name: 'Settings',
      to: isSettingsIntegrationsEnabled
        ? 'settings/integrations'
        : 'settings/user-management',
      icon: Gear,
      accessibleBy: ['admin'],
      enabled: true,
    },
  ];

  if (user) {
    primaryLinks = primaryLinks.filter(
      (link) =>
        link.accessibleBy.includes(user.role?.toLowerCase()) && link.enabled
    );
  }

  return (
    <aside
      role="complementary"
      className={twMerge(
        'w-[76px] py-[21px] flex flex-col items-center h-full bg-white border-r border-r-pv-neutral-grey-100 overflow-hidden',
        NEW_LAYOUT_UI.some((ui) => isRouteActive(ui)) && 'border-r-transparent'
      )}
    >
      <div className="flex items-center justify-center w-9 h-9 min-h-9">
        <img src="/assets/logo-1.svg" alt="petavue logo" className="mx-auto" />
      </div>
      <hr className="my-[13px] mx-auto w-[43px] border-t-pv-neutral-grey-100 border-t" />
      <nav className="h-full grid">
        <ul className="p-0 list-none flex flex-col items-center gap-2">
          {primaryLinks.map((primaryLink, key) => {
            const isLinkActive = isRouteActive(primaryLink.to);
            return (
              <li key={key}>
                <NavLink
                  className={twJoin(
                    'grid gap-1 group hover:no-underline focus:no-underline cursor-pointer',
                    primaryLink.to === 'dashboard' && 'custom-squares-four'
                  )}
                  to={primaryLink.to}
                  {...(primaryLink.trigger && {
                    onMouseDown: (e) => {
                      e.preventDefault();
                      primaryLink.trigger();
                    },
                  })}
                >
                  <div
                    className={twJoin(
                      'w-9 h-9 grid place-items-center rounded-lg group-hover:bg-pv-primary-primary-100 mx-auto',
                      isLinkActive && 'bg-pv-primary-primary-100'
                    )}
                  >
                    {primaryLink.to === 'dashboard' ? (
                      <>
                        {isLinkActive ? (
                          <primaryLink.icon.active />
                        ) : (
                          <primaryLink.icon.inactive />
                        )}
                      </>
                    ) : (
                      <primaryLink.icon
                        size={20}
                        className={twMerge(
                          'text-brand-ai-gray-300 group-hover:text-brand-ai-100 group-hover:stroke-brand-ai-100',
                          isLinkActive && 'text-brand-ai-100'
                        )}
                        {...(isLinkActive && { weight: 'fill' })}
                      />
                    )}
                  </div>
                  <span className="text-[10px] text-brand-ai-1100 font-medium leading-4 group-hover:text-brand-ai-100">
                    {primaryLink.name}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
        {/* Secondary nav links */}
        <ul className="p-0 list-none flex flex-col items-center gap-4 mt-auto">
          <li>
            <button
              className="group w-9 h-9 grid place-items-center rounded-lg hover:bg-pv-primary-primary-100 mx-auto"
              onClick={logout}
            >
              <SignOut
                size={20}
                className="text-brand-ai-gray-300 group-hover:text-brand-ai-100"
              />
            </button>
          </li>
          <li>
            <NavLink
              to="/my-profile"
              className={({ isActive }) =>
                twMerge(
                  'rounded-full bg-brand-ai-gray-500 w-10 h-10 flex items-center justify-center text-brand-ai-1100 font-medium text-sm leading-[22px] focus:no-underline hover:no-underline hover:text-brand-ai-100 border border-transparent',
                  isActive && 'border-brand-ai-100'
                )
              }
            >
              {getInitials(data?.name)}
            </NavLink>
          </li>
        </ul>
      </nav>
    </aside>
  );
};
